/* Shared admin-list pagination control.
 * Renders "Showing X-Y of Z" plus Prev/Next buttons. Calls
 * `onPageChange(newPageIndex)` (0-indexed) which the parent uses to
 * re-fetch from the backend. Disabled buttons short-circuit on the
 * boundaries so a click can't fire a redundant request. */
import { ChevronLeft, ChevronRight } from "lucide-react";

const Pagination = ({ page, pageSize, total, onPageChange, loading }) => {
  const safeTotal = Math.max(0, total || 0);
  const start = safeTotal === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(safeTotal, (page + 1) * pageSize);
  const lastPage = Math.max(0, Math.ceil(safeTotal / pageSize) - 1);
  const atStart = page <= 0;
  const atEnd = page >= lastPage;

  if (safeTotal === 0) return null;

  return (
    <nav
      data-testid="admin-pagination"
      aria-label="Pagination"
      className="mt-6 flex items-center justify-between gap-4 border-t border-[var(--ld-border)] pt-4"
    >
      <span
        data-testid="admin-pagination-summary"
        className="text-[12px] tracking-[0.12em] uppercase text-[var(--ld-ink-muted)]"
      >
        Showing <strong className="text-[var(--ld-ink)]">{start}-{end}</strong> of{" "}
        <strong className="text-[var(--ld-ink)]">{safeTotal}</strong>
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="admin-pagination-prev"
          onClick={() => !atStart && !loading && onPageChange(page - 1)}
          disabled={atStart || loading}
          className={
            "inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.18em] uppercase border transition-colors " +
            (atStart || loading
              ? "border-[var(--ld-ink)]/15 text-[var(--ld-ink-muted)]/50 cursor-not-allowed"
              : "border-[var(--ld-ink)]/40 text-[var(--ld-ink)] hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)]")
          }
        >
          <ChevronLeft size={13} strokeWidth={1.6} />
          Prev
        </button>
        <button
          type="button"
          data-testid="admin-pagination-next"
          onClick={() => !atEnd && !loading && onPageChange(page + 1)}
          disabled={atEnd || loading}
          className={
            "inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.18em] uppercase border transition-colors " +
            (atEnd || loading
              ? "border-[var(--ld-ink)]/15 text-[var(--ld-ink-muted)]/50 cursor-not-allowed"
              : "border-[var(--ld-ink)]/40 text-[var(--ld-ink)] hover:border-[var(--ld-gold-deep)] hover:text-[var(--ld-gold-deep)]")
          }
        >
          Next
          <ChevronRight size={13} strokeWidth={1.6} />
        </button>
      </div>
    </nav>
  );
};

export default Pagination;
